import React from 'react';
import Image from 'next/image';
import { ArticleCardProps, ArticleCardVariant, ArticleCardVariantConfig } from './types';
import Heading from '../Heading/Heading';
import TextLink from '../TextLink/TextLink';
import { resolveUrl } from '@/lib/utils/contentful';
import Link from 'next/link';
import { addTrailingSlashToInternalUrl } from '@/lib/utils/trailingSlash';

// Article card variants configuration
const ARTICLE_VARIANTS: Record<ArticleCardVariant, ArticleCardVariantConfig> = {
  featured: {
    article: 'flex-col h-full rounded-lg',
    image: 'aspect-video w-full',
    content: 'bg-primary-500 p-8 md:p-10 text-white',
    title: 'text-white! text-2xl! md:text-7xl! font-bold! leading-[140%]! mb-6 font-instrument',
    linkProps: { size: 'base', uppercase: true, className: 'pb-1' },
    headingType: 'H3',
    imageSizes: '(max-width: 1024px) 100vw, 58vw',
    isEager: true,
  },
  mainFeature: {
    article: 'flex-col w-[358px] h-[464px] lg:w-full rounded-[18px] overflow-hidden bg-primary-500',
    image: 'w-full h-[245px] lg:h-[310px]',
    content: 'gap-0.5 p-8 pb-[2.1875rem] max-w-113 justify-between',
    title: 'text-white! text-[1.5625rem]! font-semibold leading-[120%]! font-instrument',
    linkProps: { size: 'base', variant: 'primary', className: 'text-white! after:bottom-0' },
    headingType: 'H3',
    imageSizes: '(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw',
    isEager: false,
  },
  horizontal: {
    article: 'flex-row gap-4 h-35 items-center',
    image: 'w-55.5 h-35 rounded-xl',
    content: 'gap-[0.6875rem] w-85.75',
    title: 'text-xl! font-medium text-primary-500',
    linkProps: { size: 'base', className: 'w-fit pb-0.5 after:bottom-0' },
    headingType: 'H4',
    imageSizes: '140px',
    isEager: false,
  },
  vertical: {
    article: 'flex-col w-full max-w-95.75 lg:min-h-88',
    image: 'h-[228px] rounded-xl',
    content: 'justify-between mt-5 min-w-0',
    title: 'text-xl! font-medium text-primary-500 font-instrument leading-[130%]! break-words',
    linkProps: { size: 'base', className: 'w-fit after:bottom-0' },
    headingType: 'H4',
    imageSizes: '(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw',
    isEager: false,
  },
};

const ARTICLE_LINK_VARIANTS: Record<ArticleCardVariant, string> = {
  featured: 'block h-full rounded-lg',
  mainFeature: 'block w-[358px] max-h-[464px] lg:w-full rounded-[18px] snap-center shrink-0',
  horizontal: 'block w-full',
  vertical: 'block w-full max-w-95.75',
};

export const ArticleCard: React.FC<ArticleCardProps> = ({
  article,
  index,
  className,
  variant = 'vertical',
  linkText = 'Learn More',
  headingType,
  globalBrandLogo,
}) => {
  const currentVariant = ARTICLE_VARIANTS[variant];
  const hasHeroImage = Boolean(article.image);
  const fallbackLogo = globalBrandLogo;
  const fallbackLogoWidth = fallbackLogo?.fields?.file?.details?.image?.width || 180;
  const fallbackLogoHeight = fallbackLogo?.fields?.file?.details?.image?.height || 60;

  const articleLinkUrl = addTrailingSlashToInternalUrl(article.linkUrl);

  return (
    <Link
      href={articleLinkUrl}
      aria-label={`Learn more about ${article.title}`}
      className={`${ARTICLE_LINK_VARIANTS[variant]} min-w-0 focus:outline-none focus:ring-2 focus:ring-secondary-300 focus:ring-offset-2`}
    >
      <article
        className={`md:max-w-none flex group overflow-hidden ${currentVariant.article} ${className ?? ''}`}
      >
        {/* Image Container */}
        <div
          className={`relative overflow-hidden shrink-0 ${currentVariant.image} ${
            hasHeroImage ? 'bg-gray-100' : 'bg-tonal-bg flex items-center justify-center'
          }`}
        >
          {hasHeroImage && (
            <Image
              src={resolveUrl(article.image?.fields?.file?.url)}
              alt={article.image?.fields?.description || article.title}
              width={585}
              height={310}
              className='object-cover w-full h-full transition-transform duration-500 ease-out group-hover:scale-105'
              sizes={currentVariant.imageSizes}
              loading={index === 0 || currentVariant.isEager ? 'eager' : 'lazy'}
            />
          )}

          {!hasHeroImage && fallbackLogo && (
            <Image
              src={resolveUrl(fallbackLogo.fields.file.url)}
              alt={fallbackLogo.fields.description || `${article.title} brand logo`}
              width={fallbackLogoWidth}
              height={fallbackLogoHeight}
              className='object-contain max-w-[200px]'
              loading={index === 0 || currentVariant.isEager ? 'eager' : 'lazy'}
            />
          )}
        </div>

        {/* Content Container */}
        <div className={`flex flex-col flex-1 ${currentVariant.content}`}>
          <Heading
            data={{
              headingType: headingType || currentVariant.headingType,
              text: article.title,
              className: `${currentVariant.title} line-clamp-3 mr-8`,
            }}
          />

          <div className='w-fit inline-block border-b-2 border-secondary-500 group-hover:pb-0 group-hover:mb-2 transition-all duration-200 pb-2 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-secondary-300 focus:ring-offset-2'>
            <span
              className={`inline-block font-semibold text-global-brand-primary ${currentVariant.linkProps?.className ?? ''}`}
            >
              {linkText}
            </span>
          </div>
        </div>
      </article>
    </Link>
  );
};
